import type { Category } from './category.js'

/**
 * As regras puras da árvore de categorias, de `Docs/02-Dominio/Category.md`.
 *
 * O trade que este módulo implementa está escrito na nota: `path` é
 * **denormalizado** para que "quanto gastamos em Alimentação, incluindo tudo
 * abaixo?" seja uma consulta só —
 * `where('enrichment.categoryPath', 'array-contains', 'alimentacao')` — em vez
 * de uma leitura por nível da árvore. O preço é que mover uma categoria de pai
 * reescreve o `path` dela **e o de todos os descendentes**. Leitura barata e
 * frequente, escrita cara e rara.
 *
 * E é por isso que `rewritePathsOnMove` devolve **só o que mudou**: quem chama é
 * a Cloud Function transacional da Task 9, e documento reescrito à toa em
 * Firestore é escrita paga. Pior: essa Function é disparada pelo documento que
 * ela mesma grava, então devolver a árvore inteira num movimento que não mudou
 * nada é o laço infinito.
 *
 * ## O slug é estável, e é daí que sai `ownSlug`
 *
 * Os segmentos do `path` são **slugs estáveis, gerados na criação e nunca
 * alterados**. Renomear "Mercado" para "Supermercado" muda `name`, não o slug —
 * senão todo dado histórico quebraria, porque `enrichment.categoryPath` das
 * transações antigas guardaria a cópia denormalizada do caminho de então. O
 * campo **ainda não existe** em `Transaction` (decisão 106) e a regra vale desde
 * já: é ela que decide a forma do dado no dia em que ele existir.
 *
 * A consequência prática, e o único ponto deste arquivo que é fácil escrever
 * errado sem que nada reclame: o slug de uma categoria vem do **`path` já
 * gravado**, não de `slugify(name)`. `slugify` só é chamada quando não há path —
 * ou seja, na criação, que é literalmente o momento em que o slug é gerado.
 * Uma versão que recalcule do nome a cada recálculo passa em todo teste de
 * forma de árvore e quebra o histórico no primeiro rename.
 *
 * ## Slug estável não é slug único, e os dois são necessários
 *
 * Estável é o que `ownSlug` garante ao longo do tempo; **único entre irmãos** é
 * o que `wouldCollideWithSibling` e a terceira recusa de `rewritePathsOnMove`
 * garantem no espaço. Sem o segundo, dois "Mercado" irmãos têm `path` idêntico
 * e a consulta por ancestral soma duas categorias como uma — silenciosamente, e
 * inflando o total. Único **entre irmãos**, nunca globalmente: dois "Mercado"
 * sob pais diferentes é exatamente o que `path` existe para distinguir.
 *
 * ## O que este módulo NÃO faz
 *
 * Não fala com o banco, não sabe de `householdId` e **não carimba `updatedAt`**:
 * o relógio ambiente não entra no domínio (ver `src/shared/date/`), e quem
 * grava é que sabe a hora. As categorias que saem daqui trazem `path`, `depth` e
 * `parentId` novos e todo o resto intocado — cobrado campo a campo sobre
 * **todos** os documentos devolvidos, e não só sobre o primeiro: carimbar
 * `updatedAt` nos descendentes é o mutante que sobrevive a uma asserção que
 * olha só o movido.
 */

/**
 * O movimento colocaria a categoria dentro da própria descendência — ou a
 * árvore recebida já vem com um ciclo gravado, e subir por ela não terminaria.
 *
 * Tipo próprio, e não `Error` genérico, pelo mesmo critério de
 * `BreakdownExceedsTotalError`: é uma falha que quem chama sabe **o que fazer
 * com**. A UI de reorganização a traduz em "não dá para mover uma categoria
 * para dentro dela mesma" ao lado do seletor de pai, e a Cloud Function a
 * distingue de dado corrompido.
 */
export class CategoryCycleError extends Error {}

/**
 * O `kind` do novo pai diverge do `kind` da categoria movida — não existe
 * subcategoria de despesa dentro de uma categoria de receita.
 *
 * **Lança, nunca coage.** Herdar o `kind` do pai em silêncio reclassificaria
 * dado financeiro já lançado: uma subárvore inteira de despesas viraria receita
 * porque alguém arrastou um item na tela errada, e a soma do mês mudaria sem
 * uma palavra. Irmão de `CategoryCycleError`, e não subtipo: as duas recusas
 * têm consertos diferentes.
 */
export class CategoryKindMismatchError extends Error {}

/**
 * Já existe um irmão com esse slug sob o mesmo pai.
 *
 * **É o invariante que faltava, e sem ele a denormalização não entrega o que
 * promete.** Dois "Mercado" irmãos produzem `path` **idêntico**, e
 * `where('enrichment.categoryPath', 'array-contains', 'mercado')` — a consulta
 * que [[Category]] dá como justificativa para denormalizar `path` — soma duas
 * categorias distintas como se fossem uma. Silenciosamente, e na direção que
 * **infla** um total de gasto.
 *
 * Recusar é a resposta honesta, e **não** existe sufixo automático (`mercado-2`)
 * aqui de propósito: um slug gerado a partir da ordem de criação é uma promessa
 * de estabilidade que este módulo não teria como manter — o mesmo nome criado
 * em outra ordem daria outro slug, e `path` é dado histórico. Quem chama pede
 * outro nome.
 */
export class CategorySlugCollisionError extends Error {}

/**
 * O slug de um nome: sem acento, minúsculo, hifenizado.
 *
 * Chamada **uma vez na vida da categoria** — na criação. Depois disso o slug
 * mora no `path` e não é recalculado; ver o cabeçalho do arquivo.
 *
 * A ordem das três etapas é o que faz "Saúde & Bem-estar" virar
 * `saude-bem-estar` e não `saude-&-bem-estar` nem `saudebem-estar`: decompõe em
 * NFD e joga fora os diacríticos combinantes (o `ç` de "Serviços" é `c` +
 * U+0327, o `ã` de "Alimentação" é `a` + U+0303), depois troca **toda** corrida
 * de caractere que não seja `[a-z0-9]` por um hífen só — o `&`, os espaços
 * repetidos e a pontuação caem na mesma regra —, e por fim tira os hífens das
 * pontas.
 *
 * **Recusa nome que não produz slug** (`'☕'`, `'???'`, `'   '`). Slug vazio é o
 * pior silêncio que este módulo poderia produzir: ele iria para o `path`, e
 * `array-contains ''` casaria com toda categoria que tivesse a mesma cicatriz —
 * misturando gastos de categorias que nada têm a ver uma com a outra. `Error`
 * genérico de propósito: ao contrário do ciclo e do `kind`, não há conserto além
 * de pedir outro nome.
 */
export function slugify(name: string): string {
  const slug = name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

  if (slug === '') {
    throw new Error(
      `category-tree: "${name}" não produz slug — o nome precisa de ao menos uma letra ou dígito`,
    )
  }

  return slug
}

/**
 * O slug **estável** da categoria: o último segmento do `path` gravado.
 *
 * `slugify(name)` só entra quando não há `path` — categoria recém-criada, que é
 * o único momento em que o slug é gerado. **O cliente não escreve `path` nem no
 * `create`** (as regras negam), então esse é exatamente o estado em que a
 * Function da Task 9 encontra um documento novo.
 */
function ownSlug(category: Category): string {
  return category.path.at(-1) ?? slugify(category.name)
}

/**
 * O irmão que já ocupa este slug sob este pai, se houver.
 *
 * **Os dois lados da comparação são slugs estáveis** — `ownSlug`, e nunca
 * `slugify(candidate.name)`. Uma "Mercado" renomeada para "Supermercado"
 * continua **ocupando** o slug `mercado`, porque é ele que está no `path` das
 * transações antigas. Comparar pelo nome liberaria um slug que o histórico
 * ainda usa, que é a mesma classe de erro que `ownSlug` existe para impedir.
 *
 * **Irmão, e nunca global.** Dois "Mercado" sob pais diferentes produzem `path`
 * diferentes e são perfeitamente válidos — distinguir por caminho é o motivo
 * inteiro de `path` existir. `parentId: null` é o nível da raiz, que é um nível
 * de irmãos como qualquer outro.
 *
 * **Arquivada ocupa slug.** Decorre do invariante 3: categoria arquivada
 * continua resolvendo em dado antigo, então um irmão novo com o mesmo slug
 * deixaria esses documentos ambíguos para sempre.
 *
 * A primeira redação disto justificava com *"o slug de uma arquivada nunca é
 * liberado — slug não muda, e categoria de seed não é nem apagável"*, e **a
 * segunda metade é falsa**: `firestore.rules` só protege `isSystem`
 * (`allow delete: if isMember(hid) && categoryIsSystem(resource.data) != true`,
 * e o `create` do cliente força `isSystem == false`), então **toda categoria
 * criada pelo usuário é apagável**. A conclusão continua de pé pela razão que
 * [[Category]] já dava: quem mantém o slug vivo são as transações, não o
 * documento da categoria.
 *
 * **E é por isso que apagar é a operação perigosa aqui.** Esta função lê a lista
 * de categorias **vivas**: apague uma "Mercado" arquivada sob Alimentação e o
 * slug volta a parecer livre. O estrago tem duas formas, e a de hoje não é a de
 * amanhã. **Hoje** o que a transação guarda é o `enrichment.categoryId`, o id
 * sai de `addDoc` e a "Mercado" nova nasce com outro: o histórico da antiga não
 * migra para ela, fica apontando para um documento que não existe mais e cai
 * fora dos totais por categoria. **Quando `enrichment.categoryPath` existir** —
 * ele ainda não existe, decisão 106 —, a nova herda `['alimentacao','mercado']`
 * byte a byte e o histórico da antiga passa a somar nela, que é a ambiguidade
 * que infla gasto, entrando pela porta do **delete** em vez da porta do
 * arquivamento. Nas duas formas nada neste módulo fecha essa porta, porque a
 * prova de que o slug foi gasto não está na árvore, está nas transações.
 * **Arquivar, e não apagar**, é o que o seed já faz por regra e o que a tela de
 * categorias (Task 11) precisa fazer por escolha.
 *
 * `kind` não entra: quem colide é o `path`, e `path` não tem `kind`.
 *
 * Devolve a categoria, e não um booleano, para a mensagem do erro poder nomear
 * quem já está lá — e há teste sobre a mensagem, senão essa justificativa não
 * seria verificada por nada.
 *
 * **A resposta não pode depender da ordem da lista.** `ownSlug` **lança** para
 * um documento com `path: []` e `name` sem slug — e as regras permitem um
 * (negam `path` no `create` e nunca validam `name`). Com um `find` sobre `all`,
 * a mesma pergunta sobre a mesma árvore devolveria `true` quando o irmão
 * corrompido vem depois do achado e lançaria quando vem antes. Daí a forma
 * abaixo: recorta os irmãos primeiro e resolve **todos** os slugs deles. Irmão
 * corrompido torna a pergunta não respondível, sempre; categoria corrompida sob
 * outro pai não interfere, nunca.
 */
function siblingWithSlug(
  slug: string,
  parentId: string | null,
  all: Category[],
  ignoreId: string | null,
): Category | undefined {
  const siblings = all.filter(
    (candidate) => candidate.id !== ignoreId && candidate.parentId === parentId,
  )
  const slugs = siblings.map((sibling) => ownSlug(sibling))
  const found = slugs.indexOf(slug)

  return found === -1 ? undefined : siblings[found]
}

function byId(all: Category[]): Map<string, Category> {
  return new Map(all.map((category) => [category.id, category]))
}

function samePath(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((segment, index) => segment === b[index])
}

/**
 * O `path` da categoria: os slugs de todos os ancestrais, do mais alto ao mais
 * baixo, mais o dela própria no fim. `depth` é `path.length - 1`.
 *
 * **O `parentId` que vale é o do primeiro argumento, não o gravado em `all`** —
 * é por isso que a assinatura pede `Pick<Category,'id'|'parentId'>` em vez de
 * `Category`. Assim `rewritePathsOnMove` pergunta "qual seria o path se o pai
 * fosse este?" sem mutar nada. O `id` serve para achar em `all` o registro de
 * onde sai o slug estável.
 *
 * **Não filtra arquivada**, e isso é o invariante 3 de [[Category]]: categoria
 * arquivada não aparece em seletores, mas **continua resolvendo em dado
 * antigo**. Pular um ancestral arquivado encurtaria o `path` de toda a
 * descendência viva dele, e cada `array-contains` do histórico que passasse por
 * aquele slug deixaria de encontrar as transações.
 *
 * Três recusas, todas altas:
 *
 * - a categoria não está em `all` — sem o registro não há slug estável, e
 *   inventar um do nada é o rename silencioso que este módulo existe para
 *   impedir;
 * - um ancestral não está em `all` — o `path` sairia truncado, e um `path`
 *   truncado é um `path` que aponta para a árvore errada, não um `path`
 *   incompleto;
 * - a subida reencontra um id já visitado — `CategoryCycleError`. É dado já
 *   corrompido, não movimento, e o que importa é **terminar**: laço infinito
 *   numa Cloud Function é conta no fim do mês.
 */
export function categoryPath(
  category: Pick<Category, 'id' | 'parentId'>,
  all: Category[],
): string[] {
  const index = byId(all)
  const self = index.get(category.id)
  if (self === undefined) {
    throw new Error(`category-tree: a categoria ${category.id} não está na lista recebida`)
  }

  const ancestors: string[] = []
  const visited = new Set<string>([category.id])
  let currentId = category.parentId

  while (currentId !== null) {
    if (visited.has(currentId)) {
      throw new CategoryCycleError(
        `category-tree: a subida a partir de ${category.id} reencontra ${currentId} — a árvore recebida tem um ciclo`,
      )
    }
    visited.add(currentId)

    const parent = index.get(currentId)
    if (parent === undefined) {
      throw new Error(
        `category-tree: ${category.id} aponta para o pai ${currentId}, que não está na lista recebida`,
      )
    }

    ancestors.unshift(ownSlug(parent))
    currentId = parent.parentId
  }

  return [...ancestors, ownSlug(self)]
}

/**
 * O movimento colocaria a categoria dentro da própria descendência?
 *
 * Sobe a partir do **pai proposto**: se a categoria aparecer na subida, ela
 * seria ancestral do próprio pai, que é o ciclo. Subir custa a profundidade da
 * árvore; descer para enumerar descendentes custaria a árvore inteira.
 *
 * **"Mover para si mesma" não tem cláusula própria**, de propósito: o pai
 * proposto é a primeira parada da subida, então `newParentId === category.id`
 * já é o primeiro `true`. Uma segunda checagem antes do laço seria uma regra
 * escrita em dois lugares — e o dia em que a subida começasse errado (no pai do
 * pai proposto, o off-by-one natural aqui) ela mascararia o bug em vez de
 * revelá-lo.
 *
 * A direção é o que separa ciclo de parentesco: mover uma categoria para dentro
 * do próprio **ancestral** é subir de nível, e é legítimo — é o movimento mais
 * comum de reorganização.
 *
 * Dois casos que devolvem `false` e merecem ser ditos:
 *
 * - **pai proposto que não existe em `all`**: não há descendência para
 *   reencontrar. Quem recusa esse movimento é `rewritePathsOnMove`, com `Error`,
 *   porque o problema dele é outro — pai inexistente, não ciclo;
 * - **ciclo já gravado entre terceiros**: o conjunto de visitados encerra a
 *   subida. Não foi este movimento que o criou, e quem o denuncia é
 *   `categoryPath`, com `CategoryCycleError`.
 */
export function wouldCreateCycle(
  category: Category,
  newParentId: string | null,
  all: Category[],
): boolean {
  const index = byId(all)
  const visited = new Set<string>()
  let currentId = newParentId

  while (currentId !== null) {
    if (currentId === category.id) return true
    if (visited.has(currentId)) return false
    visited.add(currentId)
    currentId = index.get(currentId)?.parentId ?? null
  }

  return false
}

/**
 * O nome proposto produziria um slug já ocupado por um irmão sob `parentId`?
 *
 * **É a pergunta da CRIAÇÃO, e só dela.** Ele recebe **nome** e responde sobre
 * `slugify(name)` — o slug de uma categoria que ainda **não existe**. O slug de
 * uma que já existe é o **estável**, lido do `path` gravado, e renomear não o
 * toca (decisão 98). No rename as duas coisas divergem, e o predicado passa a
 * recusar o que a Cloud Function aceita: renomear "Mercado" para "Padaria"
 * tendo uma irmã "Padaria" não muda slug nenhum — `renameDocument` escreve só
 * `name` e a reserva `alimentacao_mercado` já é dela.
 *
 * A decisão 104(f) trazia esse contraexemplo, com estes nomes, e apontou o
 * predicado para o formulário da Task 11 sem dizer "só na criação"; o formulário
 * o chamou no rename, e a recusa era **silenciosa**. A revisão final da fase
 * tirou o chamador de lá. Este bloco dizia "é a pergunta da criação **e a do
 * rename**" — duas autoridades discordando dentro da mesma árvore, que é o que
 * o [[AGENTS]] resolve dando a palavra ao registro.
 *
 * **`ignoreId` fica, e hoje nenhum chamador de produção passa valor não nulo.**
 * A única pergunta correta é a da criação, onde não há id ainda; qualquer outra
 * já estaria errada por comparar o slug errado. Continua obrigatório, e não
 * opcional com default, porque é ele que obriga quem chama a dizer de que caso
 * está falando — exatamente o que não aconteceu aqui. Quem exercita o valor não
 * nulo é `category-tree.test.ts`.
 *
 * **`all` não é o último argumento, ao contrário das outras três funções deste
 * módulo, e é de propósito:** `parentId` e `ignoreId` são os dois `string |
 * null`, e deixá-los adjacentes seria convidar a troca silenciosa entre eles.
 *
 * Recebe **nome**, não slug — é o que a tela tem na mão —, então herda a recusa
 * de `slugify`: nome que não produz slug lança antes de comparar com quem quer
 * que seja.
 *
 * A recusa correspondente do lado do movimento mora em `rewritePathsOnMove`:
 * é a mesma colisão entrando pela outra porta.
 */
export function wouldCollideWithSibling(
  name: string,
  parentId: string | null,
  all: Category[],
  ignoreId: string | null,
): boolean {
  return siblingWithSlug(slugify(name), parentId, all, ignoreId) !== undefined
}

/**
 * Move `moved` para baixo de `newParentId` e devolve **só as categorias cujo
 * documento mudou** — a movida e os descendentes cujo `path` ou `depth` deixou
 * de bater —, em ordem de pai antes de filho, pronta para um lote.
 *
 * Este é o custo declarado da denormalização, e a razão de a operação viver numa
 * Cloud Function transacional: mover um pai reescreve a subárvore inteira.
 *
 * ## "Só o que mudou" é por documento, não por subárvore
 *
 * Cada nó da subárvore tem o `path` recalculado e comparado com o gravado; entra
 * no resultado quem diferir em `path`, `depth` ou `parentId`. A tentação é podar
 * — "se o path do pai não mudou, o dos filhos também não" —, e ela custaria a
 * única oportunidade de consertar um descendente cujo `path` gravado já estava
 * errado. Movimento que não move nada devolve `[]`, que é o que faz a Function
 * da Task 9 ser idempotente em vez de um laço.
 *
 * ## As três recusas, e a ordem entre elas
 *
 * 1. **ciclo** → `CategoryCycleError`;
 * 2. **`kind` divergente do novo pai** → `CategoryKindMismatchError`;
 * 3. **slug já ocupado por um irmão no destino** → `CategorySlugCollisionError`.
 *
 * Nessa ordem, e é observável. Elas vão do movimento **não representável** ao
 * **ilegal** ao **ocupado**: com um ciclo não existe `path` a calcular, então
 * responder "o pai é income" seria uma informação verdadeira e inútil; e com o
 * `kind` errado o destino é impossível por natureza, então dizer que ele está
 * ocupado não é só menos útil, é **conselho ativamente errado** — nome nenhum
 * torna aquele destino legal, e quem ouvisse "escolha outro nome" tentaria
 * quantos nomes quisesse antes de descobrir. A terceira é a única em que o
 * destino é legítimo e a resposta é "aqui não, escolha outro nome".
 *
 * As três são sobre **a aresta que este movimento cria**, e só ela:
 *
 * - um `kind` divergente já gravado dentro da subárvore não impede o
 *   movimento — quem o criou não foi esta operação, e recusar aqui deixaria a
 *   árvore corrompida e imóvel, sem nenhuma operação capaz de tirá-la de lá;
 * - a colisão é cobrada só do **movido**, porque só ele troca de irmãos. Os
 *   descendentes vão junto e continuam exatamente entre os mesmos irmãos;
 * - mover para a raiz (`newParentId === null`) nunca esbarra em `kind`, porque
 *   não há pai com quem divergir — mas **esbarra em colisão**, porque a raiz é
 *   um nível de irmãos como qualquer outro.
 *
 * A aresta é escrita mesmo quando o pai é o mesmo, então uma colisão **já
 * gravada** recusa até o movimento que não move. É deliberado, e é o mesmo
 * critério da checagem de `kind`. A árvore não fica presa: a saída é mover uma
 * das duas para outro pai — e esse é o único conserto que existe, porque o slug
 * nunca muda. Quem impede a colisão de **nascer** é a Cloud Function que cunha o
 * `path`: ela chega aqui com o `parentId` já gravado, então uma criação passa
 * exatamente por esta recusa — o slug do movido é o estável, e numa categoria
 * recém-criada o estável é `slugify(name)`. `wouldCollideWithSibling` é a
 * resposta **antecipada**, sobre o nome digitado, que o formulário dá antes de
 * gravar; ela não substitui esta, e nem poderia: a árvore que ela lê é a de um
 * instante, e quem grava é o instante seguinte.
 *
 * Os dados vêm todos de `all`, inclusive os da própria `moved`: duas fontes para
 * a mesma categoria seriam duas oportunidades de discordarem. `moved` fora de
 * `all` é `Error`, e pai novo fora de `all` também. O slug do movido é o
 * **estável** (`ownSlug`), nunca `slugify(moved.name)`: um movido renomeado
 * continua colidindo pelo slug que ele de fato tem.
 */
export function rewritePathsOnMove(
  moved: Category,
  newParentId: string | null,
  all: Category[],
): Category[] {
  const index = byId(all)
  const current = index.get(moved.id)
  if (current === undefined) {
    throw new Error(`category-tree: a categoria ${moved.id} não está na lista recebida`)
  }

  if (wouldCreateCycle(current, newParentId, all)) {
    throw new CategoryCycleError(
      `category-tree: mover ${current.id} para dentro de ${String(newParentId)} a poria dentro da própria descendência`,
    )
  }

  if (newParentId !== null) {
    const parent = index.get(newParentId)
    if (parent === undefined) {
      throw new Error(
        `category-tree: o novo pai ${newParentId} não está na lista recebida`,
      )
    }
    if (parent.kind !== current.kind) {
      throw new CategoryKindMismatchError(
        `category-tree: ${current.id} é ${current.kind} e o novo pai ${parent.id} é ${parent.kind} — kind é herdado do pai e não pode divergir`,
      )
    }
  }

  const slug = ownSlug(current)
  const occupied = siblingWithSlug(slug, newParentId, all, current.id)
  if (occupied !== undefined) {
    throw new CategorySlugCollisionError(
      `category-tree: ${current.id} tem o slug "${slug}" e ${occupied.id} já o ocupa sob ${String(newParentId)} — dois irmãos com o mesmo slug têm o mesmo path`,
    )
  }

  const childrenOf = new Map<string, Category[]>()
  for (const category of all) {
    if (category.parentId === null) continue
    const siblings = childrenOf.get(category.parentId)
    if (siblings === undefined) childrenOf.set(category.parentId, [category])
    else siblings.push(category)
  }

  const changed: Category[] = []
  // A recursão desce por `parentId`, que numa árvore corrompida pode voltar
  // para onde já esteve — `X` filho de `Y` e `Y` filho de `X`, com `X` sendo
  // movido para a raiz, passa pelas duas recusas acima e ainda assim desce para
  // sempre. Estouro de pilha é o mesmo problema do laço infinito, com um nome
  // pior.
  const seen = new Set<string>()

  const rewrite = (node: Category, path: string[], parentId: string | null): void => {
    if (seen.has(node.id)) {
      throw new CategoryCycleError(
        `category-tree: a descida a partir de ${current.id} reencontra ${node.id} — a árvore recebida tem um ciclo`,
      )
    }
    seen.add(node.id)

    const updated: Category = { ...node, parentId, path, depth: path.length - 1 }

    if (
      updated.parentId !== node.parentId ||
      updated.depth !== node.depth ||
      !samePath(updated.path, node.path)
    ) {
      changed.push(updated)
    }

    for (const child of childrenOf.get(node.id) ?? []) {
      rewrite(child, [...path, ownSlug(child)], node.id)
    }
  }

  rewrite(current, categoryPath({ id: current.id, parentId: newParentId }, all), newParentId)

  return changed
}
